import { Modal, SpaceBetween, Box, Flashbar, Spinner, Button } from "@amzn/awsui-components-react";
import { EntityType } from "@amzn/ask-legal-domain";
import * as React from "react";
import { ContentEvaluationDisclaimer } from "../common/ContentEvaluationDisclaimer";
import { ContentEvaluationResults } from "./ContentEvaluationResults";
import { ContentEvaluationModel } from "../../model/content-evaluation-model";
import { useContentEvaluation } from "../../hooks/useContentEvaluation";
import { useContainerEvaluationState } from "../../hooks/useContainerEvaluationState";
import { useFileUpload } from "../../hooks/useFileUpload";
import { ContentEvaluationUtils } from "../../utils/content-evaluation-utils";
import { ContentEvaluationForm } from "./ContentEvaluationForm";
import { ContainerEvaluationList } from "./ContainerEvaluationList";
import "../../styles/component/container/content-evaluation-modal.scss";

export const ContentEvaluationModal = (props: ContentEvaluationModel.ContentEvaluationModalProps) => {
    const [contentText, setContentText] = React.useState<string>("");
    const [activeTabId, setActiveTabId] = React.useState<string>("text");

    // Custom hooks
    const {
        evaluationResults,
        flashbarProps,
        isLoadingConfig,
        isSubmitting,
        submitSingleEvaluation,
        submitMultiContainerEvaluation,
        clearResults,
        setFlashbarProps
    } = useContentEvaluation(props.pageId);

    const {
        containerEvaluationState,
        dispatch,
        hasAnyContainerLoading,
        hasAnyContainerResults,
        scrollToContainer
    } = useContainerEvaluationState();

    const {
        selectedFile,
        isFileUploading,
        handleFileChange: handleFileUploadChange,
        extractTextFromFile,
        clearFile
    } = useFileUpload(setFlashbarProps);

    const handleFileChange = (file: File | null) => {
        // This is a simple wrapper to match the expected signature
        if (file) {
            handleFileUploadChange({ detail: { value: [file] } });
        } else {
            handleFileUploadChange({ detail: { value: [] } });
        }
    };

    const handleSubmit = async () => {
        if (activeTabId === "text" && !contentText.trim()) {
            alert("Please enter content to evaluate");
            return;
        }

        if (activeTabId === "file" && !selectedFile) {
            alert("Please select a file to evaluate");
            return;
        }

        // Clear previous results
        clearResults();
        dispatch({ type: "CLEAR_ALL" });

        try {
            let textToEvaluate = contentText;

            if (activeTabId === "file" && selectedFile) {
                try {
                    textToEvaluate = await extractTextFromFile(selectedFile);
                } catch (error) {
                    return; // Error handling is done in the hook
                }
            }

            // Container-level evaluation
            if (props.selectedContainerIds && props.selectedContainerIds.length > 0) {
                await submitMultiContainerEvaluation(
                    props.selectedContainerIds,
                    props.containerMetadata,
                    textToEvaluate,
                    dispatch
                );
            } else {
                submitSingleEvaluation(props.entityRef, textToEvaluate);
            }
        } catch (error) {
            setFlashbarProps({
                items: [{
                    type: "error",
                    content: `Failed to process file: ${error instanceof Error ? error.message : "Unknown error"}`,
                    dismissible: true,
                    onDismiss: () => setFlashbarProps(undefined)
                }]
            });
        }
    };

    const handleDismiss = () => {
        setContentText("");
        clearResults();
        clearFile();
        setActiveTabId("text");
        dispatch({ type: "CLEAR_ALL" });
        props.onDismiss();
    };

    const exportAllResultsToCSV = async () => {
        await ContentEvaluationUtils.exportEvaluationResults(
            evaluationResults,
            containerEvaluationState,
            props.selectedContainerIds,
            props.containerMetadata,
            props.pageName,
            setFlashbarProps
        );
    };

    const isLoading = isSubmitting || isFileUploading || hasAnyContainerLoading();

    return (
        <Modal
            onDismiss={handleDismiss}
            visible={props.visible}
            closeAriaLabel="Close modal"
            size="large"
            header="Content Evaluation"
            footer={
                <Box float="right">
                    <SpaceBetween direction="horizontal" size="xs">
                        <Button variant="normal" onClick={handleDismiss}>
                            Close
                        </Button>
                    </SpaceBetween>
                </Box>
            }
        >
            {flashbarProps && <Flashbar {...flashbarProps} />}
            <SpaceBetween size="m">
                {isLoadingConfig ? (
                    <Box textAlign="center">
                        <Spinner size="normal" />
                        <Box variant="p" padding={{ top: "s" }}>
                            Loading content evaluation configuration...
                        </Box>
                    </Box>
                ) : (
                    <Box>
                        <p>
                            {props.selectedContainerIds && props.selectedContainerIds.length > 0
                                ? props.selectedContainerIds.length > 1
                                    ? "Enter the content you want to evaluate against the selected containers' rules and guidelines:"
                                    : "Enter the content you want to evaluate against the selected container's rules and guidelines:"
                                : props.entityRef.type === EntityType.Page
                                    ? "Enter the content you want to evaluate against the page's rules and guidelines:"
                                    : "Enter the content you want to evaluate against the page draft's rules and guidelines:"}
                        </p>
                    </Box>
                )}

                <ContentEvaluationDisclaimer />

                <ContentEvaluationForm
                    contentText={contentText}
                    onContentTextChange={setContentText}
                    activeTabId={activeTabId}
                    onTabChange={setActiveTabId}
                    selectedFile={selectedFile}
                    onFileChange={handleFileChange}
                    onSubmit={handleSubmit}
                    isSubmitting={isLoading}
                    onFlashbarUpdated={setFlashbarProps}
                />

                {/* Page-Level Results */}
                {evaluationResults && !props.selectedContainerIds && (
                    <Box>
                        <SpaceBetween size="m">
                            <Box>
                                <Button
                                    variant="normal"
                                    iconName="download"
                                    onClick={exportAllResultsToCSV}
                                    disabled={!evaluationResults?.findings || evaluationResults.findings.length === 0}
                                >
                                    Export Results
                                </Button>
                            </Box>

                            <ContentEvaluationResults
                                results={evaluationResults}
                                onFlashbarUpdated={setFlashbarProps}
                            />
                        </SpaceBetween>
                    </Box>
                )}

                {/* Container Results */}
                {props.selectedContainerIds && Object.keys(containerEvaluationState).length > 0 && (
                    <Box>
                        <Box margin={{ bottom: "m" }}>
                            <Button
                                variant="normal"
                                iconName="download"
                                onClick={exportAllResultsToCSV}
                                disabled={hasAnyContainerLoading() || !hasAnyContainerResults()}
                            >
                                Export Results
                            </Button>
                        </Box>

                        <ContainerEvaluationList
                            selectedContainerIds={props.selectedContainerIds!}
                            containerMetadata={props.containerMetadata || {}}
                            containerEvaluationState={containerEvaluationState}
                            onScrolledToContainer={scrollToContainer}
                            onFlashbarUpdated={setFlashbarProps}
                        />
                    </Box>
                )}
            </SpaceBetween>
        </Modal>
    );
};
